import { useEffect, useMemo, useState } from 'react';
import type { ContentApi } from '@wg/api/content';
import type { GuestContent } from '@wg/types/content';

/**
 * Hardcoded fallback used when the /content/guest endpoint is unreachable.
 * Mirrors the RN useGuestContent.ts FALLBACK_CONTENT exactly so guests see
 * consistent copy across clients even on a network blip.
 */
export const GUEST_FALLBACK_CONTENT: GuestContent = {
  schema: 1,
  languageRequested: 'en-US',
  languageResolved: 'en-US',
  fallback: true,
  updatedAt: new Date(0).toISOString(),
  cacheSeconds: 3600,
  banner: {
    text: 'Limited features — Create free account',
    ctaText: 'Create Free Account',
  },
  drawer: {
    title: 'Unlock with a free account',
    features: [
      { icon: '✨', text: 'Smart, tailored tips for your adventure' },
      { icon: '🗣️', text: 'Unlimited questions about your destination' },
      { icon: '☁️', text: 'Sync across every device' },
    ],
    quickSignupText: 'Quick signup. 100% free.',
    createAccountCta: 'Create Free Account',
    signInCta: 'Sign In',
  },
};

export interface UseGuestUXOptions {
  /** From AuthContext / sessionInfo. */
  isGuest: boolean;
  /** From AuthContext / sessionInfo. JWT-claim hydration of profile. */
  firstName?: string | null;
  /** Profile (can be null while loading). Used to suppress greeting comma when empty. */
  userFirstName?: string | null;
  /** Currently selected language (BCP-47). */
  language?: string;
  /** Inject @wg/api/content's createContentApi(http) result. Optional —
   *  callers that don't want server-driven copy can pass undefined and get
   *  fallback content. */
  contentApi?: ContentApi | null;
}

export interface GuestUX {
  isGuest: boolean;
  /** True when the dashboard greeting should not include a name. */
  shouldOmitName: boolean;
  /** Whether the CTA banner should appear (guest + content available). */
  shouldShowBanner: boolean;
  /** Server-driven content with fallback. Always set for guests. */
  guestContent: GuestContent;
  isContentLoading: boolean;
}

/**
 * Hook that consolidates guest-aware UI gating + server-driven copy. Both web
 * and RN can use this to drive: dashboard CTA banner, avatar tap drawer,
 * quota-exhausted modal, greeting omission.
 *
 * Logic mirrors apps/WunderGuide/hooks/useGuestContent.ts. Fallback content
 * is identical so a network failure produces consistent copy across clients.
 */
export function useGuestUX(options: UseGuestUXOptions): GuestUX {
  const {
    isGuest,
    userFirstName,
    firstName,
    language = 'en-US',
    contentApi,
  } = options;
  const [guestContent, setGuestContent] = useState<GuestContent>(
    GUEST_FALLBACK_CONTENT,
  );
  const [isContentLoading, setIsContentLoading] = useState<boolean>(isGuest);

  useEffect(() => {
    let cancelled = false;
    if (!isGuest) {
      setIsContentLoading(false);
      return;
    }
    if (!contentApi) {
      setGuestContent(GUEST_FALLBACK_CONTENT);
      setIsContentLoading(false);
      return;
    }
    setIsContentLoading(true);
    contentApi
      .fetchGuestContent(language)
      .then(content => {
        if (!cancelled) setGuestContent(content);
      })
      .catch(() => {
        if (!cancelled) setGuestContent(GUEST_FALLBACK_CONTENT);
      })
      .finally(() => {
        if (!cancelled) setIsContentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isGuest, language, contentApi]);

  return useMemo<GuestUX>(() => {
    // RN's Dashboard *unconditionally* drops the comma + name for guests —
    // even when the JWT carries something like "Guest" in user_metadata.
    // For authed users we only suppress when there's truly nothing to show.
    // Mirrors apps/WunderGuide/views/Dashboard.tsx greeting useMemo:
    //   if (isGuest) return `${greetingText}! 🌺`;
    //   return `${greetingText}${me?.firstName ? `, ${me.firstName}` : ''}…`
    const resolvedName = userFirstName || firstName || '';
    return {
      isGuest,
      shouldOmitName: isGuest || !resolvedName.trim(),
      shouldShowBanner: isGuest,
      guestContent,
      isContentLoading,
    };
  }, [isGuest, userFirstName, firstName, guestContent, isContentLoading]);
}
